import type {
  AgentQuery,
  CreateAgent,
  PaginatedAgentsResponse,
  UpdateAgent,
} from '@monorepo/shared/schemas/agents'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { agentService } from '../services/agentService'
import type { Agent as UiAgent } from '../types/agent.types'

// Query keys
export const agentKeys = {
  all: ['agents'] as const,
  lists: () => [...agentKeys.all, 'list'] as const,
  list: (filters?: AgentQuery) => [...agentKeys.lists(), filters] as const,
  details: () => [...agentKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentKeys.details(), id] as const,
  actions: (id: string) => [...agentKeys.detail(id), 'actions'] as const,
}

// Agents list query (returns full paginated response)
export function useAgents(
  filters?: AgentQuery,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: agentKeys.list(filters),
    queryFn: () => agentService.getAgents(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled,
  })
}

// Convenience hook that returns just the agents array and pagination separately
// Use this when you don't need the full response structure
export function useAgentsList(
  filters?: AgentQuery,
  options?: { enabled?: boolean }
) {
  const query = useAgents(filters, options)
  const agents = (query.data?.data || []) as UiAgent[]
  return {
    ...query,
    agents,
    pagination: query.data?.pagination,
  }
}

// Infinite agents query for virtualized table
export function useInfiniteAgents(filters?: Omit<AgentQuery, 'page'>) {
  return useInfiniteQuery({
    queryKey: agentKeys.list(filters),
    queryFn: ({ pageParam = 1 }) =>
      agentService.getAgents({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      // Check if there's a next page in pagination response
      return lastPage.pagination.has_next
        ? lastPage.pagination.page + 1
        : undefined
    },
    staleTime: 5 * 60 * 1000,
  })
}

// Single agent query
export function useAgent(id: string) {
  return useQuery({
    queryKey: agentKeys.detail(id),
    queryFn: () => agentService.getAgent(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

// Mutations
export function useCreateAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAgent) => agentService.createAgent(data),
    onSuccess: newAgent => {
      // Invalidate and refetch agents list
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })

      // Add the new agent to existing cache
      queryClient.setQueryData(agentKeys.detail(newAgent.id), newAgent)

      toast.success(`"${newAgent.name}" has been created successfully.`)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useUpdateAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAgent }) => {
      return await agentService.updateAgent(id, data)
    },
    onSuccess: updatedAgent => {
      // Safety check for the updatedAgent response
      if (!updatedAgent || !updatedAgent.id) {
        toast.error('Invalid response from server')
        return
      }

      // Update the specific agent in cache
      queryClient.setQueryData(agentKeys.detail(updatedAgent.id), updatedAgent)

      // Update any cached agents list queries with the updated agent
      const matchingLists = queryClient.getQueriesData({
        queryKey: agentKeys.lists(),
      })
      for (const [key] of matchingLists) {
        queryClient.setQueryData(key, (existing: unknown) => {
          // Paginated response: { data: AgentResponse[], pagination: {...} }
          if (
            existing &&
            typeof existing === 'object' &&
            'data' in existing &&
            Array.isArray((existing as { data: unknown }).data)
          ) {
            const paginated = existing as PaginatedAgentsResponse
            return {
              ...paginated,
              data: paginated.data.map(a =>
                a.id === updatedAgent.id ? updatedAgent : a
              ),
            }
          }
          // Infinite list: { pages: PaginatedAgentsResponse[], pageParams: unknown[] }
          if (
            existing &&
            typeof existing === 'object' &&
            'pages' in existing &&
            Array.isArray((existing as { pages: unknown }).pages)
          ) {
            const inf = existing as {
              pages: PaginatedAgentsResponse[]
              pageParams: unknown[]
            }
            return {
              ...inf,
              pages: inf.pages.map(page => ({
                ...page,
                data: page.data.map(a =>
                  a.id === updatedAgent.id ? updatedAgent : a
                ),
              })),
            }
          }
          return existing
        })
      }

      // Invalidate related queries to force refresh where needed
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })
      // Also invalidate the specific agent detail query to ensure it refetches
      queryClient.invalidateQueries({
        queryKey: agentKeys.detail(updatedAgent.id),
      })

      toast.success(`"${updatedAgent.name}" has been updated successfully.`)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useDeleteAgent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => agentService.deleteAgent(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: agentKeys.detail(id) })

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() })

      toast.success('The agent has been deleted successfully.')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
